import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { SetStateAction, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableLoading,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { CSVLink } from "react-csv";
import { Route as ReportAvailableRoute } from "../index";
import {
  CaretSortIcon,
} from "@radix-ui/react-icons";
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { useExpiryListData } from "../-hooks/useExpiryListData";

const parseDate = (
  date:
    | string
    | number
    | Date
    | dayjs.Dayjs
    | { Time: string; Valid: boolean }
    | null
    | undefined
) => {
  if (typeof date === "string") {
    return dayjs(date, "YYYY-MM-DD");
  }
  return dayjs();
};

const PaginationButton = ({ 
  children, 
  active, 
  disabled, 
  onClick 
}: { 
  children: React.ReactNode
  active?: boolean
  disabled?: boolean
  onClick: () => void 
}) => (
  <Button
    variant={active ? "outline" : "outline"}
    size="sm"
    disabled={disabled}
    onClick={onClick}
    className={`h-8 w-8 p-2 ${
      active 
        ? "bg-blue-600 hover:bg-blue-400 text-white" 
        : "hover:bg-blue-500 hover:text-white"
    }`}
  >
    <span className="flex items-center justify-center w-full h-full">
      {children}
    </span>
  </Button>
)

const Component = () => {
  const searchParams = ReportAvailableRoute.useSearch();
  const navigate = useNavigate();
  
  const { data, isLoading, error, isFetching } = useExpiryListData();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("expiry_date");

  const handleRequestSort = (property: SetStateAction<string>) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = data?.items ? data.items.slice().sort((a, b) => {
    if (orderBy === "expiry_date") {
      const dateA = parseDate(a.expiryDate?.toDate()).valueOf();
      const dateB = parseDate(b.expiryDate?.toDate()).valueOf();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  }) : [];

  const headers = [
    { label: "Trade Name", key: "trade_name" },
    { label: "Unit Type", key: "unit_type" },
    { label: "Barcode", key: "barcode" },
    { label: "Price", key: "price" },
    { label: "Cost", key: "cost" },
    { label: "Available Stock (Unit per stock)", key: "available_stock" },
    { label: "Batch Number", key: "batch_number" },
    { label: "Expiry Date", key: "expiry_date" },
    { label: "Last Sold On", key: "last_sold_date" },
  ];

  const csvData = data?.items ? data.items.map((item) => ({
    trade_name: item.tradeName,
    unit_type: item.unitType,
    barcode: item.barcode ? item.barcode : "Not Available",
    price: convertToCurrencyRounded(item.sellingPrice),
    cost: convertToCurrencyRounded(item.costFromSupplier),
    available_stock: `${item.availableQuantity} (${item.unit})`,
    batch_number: item.batchNumber,
    expiry_date: parseDate(item.expiryDate?.toDate()).format("DD/MM/YYYY"),
    last_sold_date: "Not Available",
  })) : [];

  const filename = "exported_data.csv";

  const itemsPerPage = searchParams.pageSize;
  const totalPages = data?.totalCount && data.totalCount > 0 ? Math.ceil(data.totalCount / itemsPerPage) : 1;
  
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (searchParams.page > 3) pages.push('...');
      for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (searchParams.page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const columns = [
    { key: "trade_name", label: "Trade Name" },
    { key: "unit_type", label: "Unit Type" },
    { key: "barcode", label: "Barcode" },
    { key: "price", label: "Price" },
    { key: "cost", label: "Cost" },
    { key: "available_stock", label: "Available Stock" },
    { key: "batch_number", label: "Batch Number" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "last_sold_on", label: "Last Sold On" },
  ];

  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => {
                    navigate({
                      to: "/branch/reports",
                    });
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Reports Overview
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Expiry List</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <CSVLink data={csvData} headers={headers} filename={filename}>
            <Button>Export List</Button>
          </CSVLink>
        </div>
        <hr className="my-4" />

        <div className="border rounded-lg bg-white overflow-hidden">
          {isLoading ? (
            <TableLoading columns={columns} visibleColumns={visibleColumns} />
          ) : !data?.items || data.items.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">No entries found</div>
            </div>
          ) : (
            <>
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="text-sm">
                    <TableHead className={`${tableHeadClass}`}>Trade Name</TableHead>
                    <TableHead className={`${tableHeadClass}`}>Unit Type</TableHead>
                    <TableHead className={`${tableHeadClass}`}>Barcode</TableHead>
                    <TableHead className={`${tableHeadClass}`}>Price</TableHead>
                    <TableHead className={`${tableHeadClass}`}>Cost</TableHead>
                    <TableHead className={`${tableHeadClass}`}>Available Stock <br /> (Unit per stock)</TableHead>
                    <TableHead className={`${tableHeadClass}`}>Batch Number</TableHead>
                    <TableHead className={`${tableHeadClass}`}
                      onClick={() => handleRequestSort("expiry_date")}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="flex items-center gap-2">
                        <span>Expiry Date</span>
                        {orderBy === "expiry_date" && (
                          <CaretSortIcon
                            className={`ml-2 h-5 w-5 ${order === "asc" ? "rotate-180" : ""}`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className={`${tableHeadClass}`}>Last Sold On</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((item, index) => (
                    <TableRow key={index} className="font-medium text-sm">
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>{item.tradeName}</TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>{item.unitType}</TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>
                        {item.barcode ? item.barcode : "Not Available"}
                      </TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>{convertToCurrencyRounded(item.sellingPrice)}</TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>{convertToCurrencyRounded(item.costFromSupplier)}</TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>
                        {item.availableQuantity} ({item.unit})
                      </TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>{item.batchNumber}</TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>
                        {parseDate(item.expiryDate?.toDate()).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell className={`${tableCellClass} ${index === sortedData.length - 1 ? tableCellLastRowClass : ""}`}>
                        {"Not Available"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
            <p>{data?.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page - 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </PaginationButton>
              {getPageNumbers().map((page, idx) => (
                page === '...'
                  ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                  : <PaginationButton
                      key={page}
                      active={searchParams.page === page}
                      onClick={() => {
                        navigate({
                          search: {
                            ...searchParams,
                            page: typeof page === 'number' ? page : searchParams.page
                          }
                        });
                      }}
                    >
                      <span className="text-sm">{page}</span>
                    </PaginationButton>
              ))}
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page + 1
                    }
                  });
                }}
                disabled={searchParams.page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: totalPages
                    }
                  });
                }}
                disabled={searchParams.page === totalPages}
              >
                <ChevronsRight className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;